import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { merge } from 'lodash-es'
import { useTheme } from 'styled-components/native'

import { secureMicrositesMetadataList } from '@lyrahealth-inc/shared-app-logic/src/features/CMSMicrosite/constants'
import { CMSMicrositeMetadata } from '@lyrahealth-inc/shared-app-logic/src/features/CMSMicrosite/types'
import { IS_PRODUCTION } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { PAGE_ROUTES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { useHealthPlanAndEligibilityData } from '@lyrahealth-inc/shared-app-logic/src/features/devTools/hooks/useHealthPlanAndEligibilityData'
import {
  getIsMixpanelDebugEnabled,
  getUniversalSearchConfig,
  getUniversalSearchDebugSettings,
  resetUniversalSearchConfigWeb,
  setUniversalSearchConfigWeb,
  toggleEnableMixpanelDebugWeb,
  toggleUniversalSearchShowDebugWeb,
} from '@lyrahealth-inc/shared-app-logic/src/features/devTools/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { DirectLinkSource } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import {
  DEFAULT_UNIVERSAL_SEARCH_CONFIG,
  UniversalSearchConfig,
} from '@lyrahealth-inc/shared-app-logic/src/features/universalSearch/constants'
import { WELLNESS_TOOLKIT_PROMOTION } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessToolkit/types'
import { TriageSearch } from '@lyrahealth-inc/shared-app-logic/src/models/searchForCare/SearchForCare'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { DevTools, devToolsSidePanelStyle } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/devTools/DevTools'

import { useDevTools } from './DevToolsProvider'
import { useActiveMicrositesMetadataList } from '../../common/hooks/useActiveMicrositesMetadataList'
import { useNavigateToForkedTriage } from '../../common/hooks/useNavigateToForkedTriage'
import { useNavigateToSearchForCare } from '../../common/hooks/useNavigateToSearchForCare'
import { useNavigateToSearchForCareChild } from '../../common/hooks/useNavigateToSearchForCareChild'
import { useNavigateToWellnessToolkit } from '../../common/hooks/useNavigateToWellnessToolkit'
import { getMixpanelActivity } from '../../data/appGlobals/appGlobalsSelectors'
import {
  getCustomerName,
  getGuidedSelfCareEnabled,
  getIsAUDSupported,
  getIsBlendedCareMedsAvailable,
  getIsClinicalLeaveEvaluationEnabled,
  getIsCoachingEnabled,
  getIsCoachingForParentsEnabled,
  getIsDAMedsEnabled,
  getIsDirectAccessTherapyEnabled,
  getIsLCTherapyEnabled,
  getProgramCoverageBreakdown,
} from '../../data/customer/customerSelectors'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import {
  getId,
  getIsUserLoggedIn,
  getUserChildren,
  getUserCountryCode,
  getUserDisplayLanguageOrDefault,
  getUsername,
} from '../../data/user/userSelectors'
import { getHealthPlan, getSelectedHealthPlanConfig } from '../healthPlan/data/healthPlanSelectors'
import { getOnboardState } from '../onboard/data/onboardSelectors'
import { deleteAllTriageSearches } from '../triage/data/triageActions'
import { getTriageState } from '../triage/data/triageSelectors'

export const DevToolsModal: React.FC = () => {
  const { isDevToolsOpen, closeDevTools } = useDevTools()
  const isLoggedIn = useSelector(getIsUserLoggedIn)
  const { breakpoints } = useTheme()
  const dispatch = useAppDispatch()

  // Account Details
  const userId = useSelector(getId)
  const email = useSelector(getUsername)
  const country = useSelector(getUserCountryCode)
  const language = useSelector(getUserDisplayLanguageOrDefault)
  const customerName = useSelector(getCustomerName)
  const dependents = useSelector(getUserChildren)?.toJS()

  // Feature Flags
  const featureFlags = useFlags()
  const enableMixpanelDebug = getIsMixpanelDebugEnabled()

  // Universal Search debug settings (localStorage + React state for UI updates)
  const initialUniversalSearchSettings = getUniversalSearchDebugSettings()
  const [universalSearchShowDebug, setUniversalSearchShowDebug] = useState(initialUniversalSearchSettings.showDebug)

  const handleToggleUniversalSearchShowDebug = () => {
    toggleUniversalSearchShowDebugWeb()
    setUniversalSearchShowDebug((prev) => !prev)
  }

  const { universalSearchConfig: ldConfigRaw } = useFlags()
  // Deep merge config: DevTools > LaunchDarkly > defaults
  const mergedConfig: UniversalSearchConfig = useMemo(() => {
    const devToolsOverrides = getUniversalSearchConfig()
    return merge({}, DEFAULT_UNIVERSAL_SEARCH_CONFIG, ldConfigRaw, devToolsOverrides)
  }, [ldConfigRaw])
  const [universalSearchConfig, setUniversalSearchConfigLocal] = useState<UniversalSearchConfig>(mergedConfig)

  const handleUniversalSearchConfigChange = (config: UniversalSearchConfig) => {
    setUniversalSearchConfigWeb(config)
    setUniversalSearchConfigLocal(config)
  }

  const handleResetUniversalSearchConfig = () => {
    resetUniversalSearchConfigWeb()
    // Reload required: localStorage isn't reactive, so the search page won't pick up the change without a reload
    window.location.reload()
  }

  // Health Plan / Eligibility
  const isCoachingEnabled = useSelector(getIsCoachingEnabled)
  const isCoachingForParentsEnabled = useSelector(getIsCoachingForParentsEnabled)
  const isBlendedCareMedsAvailable = useSelector(getIsBlendedCareMedsAvailable)
  const isDAMedsAvailable = useSelector(getIsDAMedsEnabled)
  const isDirectAccessTherapySupported = useSelector(getIsDirectAccessTherapyEnabled)
  const isGuidedSelfCareEnabled = useSelector(getGuidedSelfCareEnabled)
  const isLCTherapyAvailable = useSelector(getIsLCTherapyEnabled)
  const isClinicalLeaveEvaluationEnabled = useSelector(getIsClinicalLeaveEvaluationEnabled)
  const isAlcoholUseDisorderTherapyEnabled = useSelector(getIsAUDSupported)
  const programCoverageBreakdown = useSelector(getProgramCoverageBreakdown).toJS()
  const healthPlanDataImmutable = useSelector(getHealthPlan)
  const healthPlanData = healthPlanDataImmutable ? healthPlanDataImmutable.toJS() : undefined
  const healthPlanConfigImmutable = useSelector(getSelectedHealthPlanConfig)
  const healthPlanConfig = healthPlanConfigImmutable ? healthPlanConfigImmutable.toJS() : undefined

  // Search for Care Data
  const onboardData = useSelector(getOnboardState).toJS()
  const triageData = useSelector(getTriageState).toJS()

  const searchForCareData = useMemo(() => {
    if (triageData.searches) {
      triageData.currentSearch = triageData.searches.find(
        (search: TriageSearch) => search.id === triageData.selectedSearchId,
      )
      if (triageData.currentSearch) {
        triageData.currentSearchQuestionnaires = triageData.currentSearch.body?.questionnaires
      }
    }

    return {
      onboardData,
      triageData,
      onboardExclude: ['appointmentBooking', 'triageHealthPlan', 'individualTherapy'],
      triageDataExclude: [
        'screeners',
        'wordCloud',
        'fetchedMetadataVersion',
        'getAllTriageSearchesState',
        'triageMetadata',
      ],
      triagePriorityOrder: ['selectedSearchId', 'searcherInfo', 'currentSearchQuestionnaires', 'currentSearch'],
      onboardPriorityOrder: [],
    }
  }, [onboardData, triageData])

  const navigate = useNavigate()
  const navigateToSearchForCare = useNavigateToSearchForCare()
  const { navigateToChildSearch: navigateToChildSearchForCare } = useNavigateToSearchForCareChild()
  const navigateToForkedTriage = useNavigateToForkedTriage()
  const navigateToWellnessToolkit = useNavigateToWellnessToolkit()
  const activeMicrositesMetadataList = useActiveMicrositesMetadataList()

  const allHealthPlanData = useHealthPlanAndEligibilityData({
    programCoverageBreakdown,
    isAlcoholUseDisorderTherapyEnabled,
    isBlendedCareMedsAvailable,
    isLCTherapyAvailable,
    isCoachingEnabled,
    isCoachingForParentsEnabled,
    isDAMedsAvailable,
    isDirectAccessTherapySupported,
    isGuidedSelfCareEnabled,
    isClinicalLeaveEvaluationEnabled,
    healthPlanData,
    healthPlanConfig,
  })

  const healthPlanAndEligibilityData = useMemo(() => {
    return {
      ...allHealthPlanData,
      healthPlanInformation: {
        ...allHealthPlanData.healthPlanInformation,
        healthPlanExclude: ['user', 'isPatientDataFetched', 'legalDob', 'eligibilityOptions'],
        healthPlanSections: [
          {
            title: 'Payment',
            properties: [
              'infractionCardLast4Type',
              'cardBrand',
              'cardLast4',
              'cardLast4Type',
              'infractionCardBrand',
              'infractionCardLast4',
            ],
          },
          {
            title: 'Sessions',
            properties: ['healthPlanSessionLimit', 'usedSessionCount', 'usedSessionCountByProgram', 'sessionHistory'],
          },
        ],
        healthPlanPriorityOrder: ['healthPlanName'],
      },
    }
  }, [allHealthPlanData])

  // Mixpanel
  const mixpanelActivity = useSelector(getMixpanelActivity)

  const handleNavigateToToolkit = (promotion: WELLNESS_TOOLKIT_PROMOTION) => {
    navigate(PAGE_ROUTES.HOMEBASE) // Toolkit content won't reload if navigating between toolkits directly, so navigate to homebase to "refresh"
    setTimeout(() => {
      navigateToWellnessToolkit({ toolkitPromotion: promotion, directLinkSource: DirectLinkSource.DEV_TOOLS })
    }, 100)
  }

  // Navigation handlers for DevTools
  const handleNavigateToCourseCompletionCertScreen = () => {
    // Generate and display a course completion certificate
    // Using the existing getCourseCompletionCertificate functionality
    import('../../common/http/data/libraryCourseCertificate')
      .then(({ getCourseCompletionCertificate }) => {
        if (userId) {
          getCourseCompletionCertificate(userId, 'Dev Tools Test Course', new Date())
        } else {
          console.log('User ID not available for certificate generation')
        }
      })
      .catch((error) => {
        console.error('Failed to load certificate functionality:', error)
      })
  }

  const handleNavigateToElectionMicrosite = () => {
    navigate(PAGE_ROUTES.ELECTION)
  }

  const handleNavigateToCMSMicrosite = (metadata: CMSMicrositeMetadata) => {
    const isSecure = secureMicrositesMetadataList.find((item) => item.micrositeId === metadata.micrositeId)
    navigate(`${isSecure ? '/secure/index' : ''}/${metadata.micrositeUrl}`)
  }

  const handleNavigateToSetUpAccount = () => {
    navigate(PAGE_ROUTES.REGISTER)
  }

  const handleNavigateToNeurodiversityToolkit = () => {
    navigate(PAGE_ROUTES.CENTER_OF_EXCELLENCE.NEURODIVERSITY.LANDING_PAGE)
  }

  const handleNavigateToWomensHealthToolkit = () => {
    navigate(PAGE_ROUTES.CENTER_OF_EXCELLENCE.WOMENS_HEALTH.LANDING_PAGE)
  }

  const handleNavigateToHealthChallengesToolkit = () => {
    navigate(PAGE_ROUTES.CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES.LANDING_PAGE)
  }

  const handleNavigateToUniversalSearch = () => {
    navigate('/universal-search')
  }

  const accountDetails = useMemo(() => {
    return {
      userId,
      email,
      customerName,
      country,
      language,
      dependents,
    }
  }, [userId, email, customerName, country, language, dependents])

  const handleDownloadDevToolsData = () => {
    const data = {
      accountDetails,
      featureFlags,
      allHealthPlanData,
      triageData,
      onboardData,
      mixpanelActivity,
      timestamp: new Date().toISOString(),
    }

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `devtools-data-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleDeleteAllSearches = () => {
    dispatch(deleteAllTriageSearches(userId))
  }

  if (IS_PRODUCTION || !isDevToolsOpen) {
    return null
  }

  return (
    <Modal
      visible={isDevToolsOpen}
      onRequestClose={closeDevTools}
      isSidePanel={!breakpoints.isMobileSized}
      scrollable
      customSidePanelStyle={devToolsSidePanelStyle}
      useWebPortalForBottomSheet
      modalContents={
        <DevTools
          isLoggedIn={isLoggedIn}
          accountDetails={accountDetails}
          featureFlags={featureFlags}
          idTokenExpiration={0}
          inactivityExpiration={0}
          debugLang=''
          enableMixpanelDebug={enableMixpanelDebug}
          onClose={closeDevTools}
          onNavigateToSearchForCare={navigateToSearchForCare}
          onNavigateToChildSearchForCare={navigateToChildSearchForCare}
          onNavigateToForkedTriage={() => {
            navigateToForkedTriage()
          }}
          onNavigateToCourseCompletionCertScreen={handleNavigateToCourseCompletionCertScreen}
          onNavigateToElectionMicrosite={handleNavigateToElectionMicrosite}
          onNavigateToCMSMicrosite={handleNavigateToCMSMicrosite}
          onNavigateToSetUpAccount={handleNavigateToSetUpAccount}
          onNavigateToUniversalSearch={handleNavigateToUniversalSearch}
          onNavigateToToolkit={handleNavigateToToolkit}
          onNavigateToNeurodiversityToolkit={handleNavigateToNeurodiversityToolkit}
          onNavigateToWomensHealthToolkit={handleNavigateToWomensHealthToolkit}
          onNavigateToHealthChallengesToolkit={handleNavigateToHealthChallengesToolkit}
          onToggleEnableMixpanelDebug={toggleEnableMixpanelDebugWeb}
          universalSearchShowDebug={universalSearchShowDebug}
          onToggleUniversalSearchShowDebug={handleToggleUniversalSearchShowDebug}
          universalSearchConfig={universalSearchConfig}
          universalSearchBaseConfig={mergedConfig}
          onUniversalSearchConfigChange={handleUniversalSearchConfigChange}
          onResetUniversalSearchConfig={handleResetUniversalSearchConfig}
          healthPlanAndEligibilityData={healthPlanAndEligibilityData}
          mixpanelActivity={mixpanelActivity}
          onDownloadDevToolsData={handleDownloadDevToolsData}
          searchForCareData={searchForCareData}
          activeMicrositesMetadataList={activeMicrositesMetadataList}
          onPressDeleteAllSearches={handleDeleteAllSearches}
        />
      }
    />
  )
}
